import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/UpdateSKUModal";
// A folha deste modal é anterior à casca compartilhada e não tem AlertMessage,
// nem o selo de campo calculado, nem a caixa da linha de conversão. Em vez de
// copiar os três para cá (que é como as seis folhas de modal viraram cópias
// divergentes), importa-se a casca ao lado, só para o que falta.
import * as Casca from "../../../styles/components/configuration/modals/_casca";
import {
  EntradaDeConversao,
  RENDIMENTO_INVALIDO,
  UNIDADES_DE_COMPRA,
  UNIDADES_DE_CONSUMO,
  UNIDADES_DE_ESTOQUE,
  VALOR_OUTRA_UNIDADE_DE_COMPRA,
  montarLinhaDeConversao,
  paraNumero,
  rendimentoEhValido,
} from "./sku/unidades";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

// Literal e permanente: o servidor não recusa campo desconhecido, descarta
// calado. Um PATCH que responde 200 tendo jogado metade do formulário fora é
// indistinguível de um salvamento bom por este lado.
//
// A lista de `updateOrganizationInfo` foi ampliada e hoje aceita custo, peso,
// dimensões, as três unidades, os dois fatores, perecível, validade controlada
// e estoque de segurança. O que sobrou de fora é UM campo — e o aviso diz qual,
// em vez de assustar com "metade do formulário".
const AVISO_LISTA_FECHADA =
  "Uma exceção conhecida: o campo Observações ainda não está na lista de " +
  "campos que o PATCH /organizationInfo/:uuid aceita, então ele é descartado " +
  "EM SILÊNCIO pelo servidor. Todo o resto desta tela grava.";

const FORM_VAZIO = {
  skuUUID: "",
  skuStatus: true,
  skuType: "",
  skuCode: "",
  skuName: "",
  skuUnitOfMeasure: "",
  skuSuggestedPrice: "",
  skuBarcode: "",
  skuCost: "",
  skuWeight: "",
  skuDimensions: "",
  skuNotes: "",
  // Compra, estoque e consumo são TRÊS unidades diferentes do mesmo SKU:
  // compra-se caixa, guarda-se kg, consome-se g. O `skuUnitOfMeasure` sozinho
  // nunca deu conta disso.
  unidadeCompra: "",
  unidadeCompraOutra: "",
  unidadeEstoque: "",
  unidadeConsumo: "",
  fatorConversaoCompraEstoque: "",
  fatorRendimento: "",
  perecivel: false,
  validadeControlada: false,
  estoqueSeguranca: "",
  // productServiceTax NÃO entra aqui de propósito: é false em 504 de 504
  // registros reais. Campo que nunca varia não é configuração, é ruído — ocupa
  // linha de formulário e sugere uma decisão que ninguém toma.
};

interface UpdateSKUModalProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue?: (infoValue: string) => any;
  decryptedInfoToken?: any;
}

const UpdateSKUModal: React.FC<UpdateSKUModalProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-updateskumodal",
  });

  const [form, setForm] = useState(FORM_VAZIO);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    // A condição era `if (selectedObject)`, e `{}` é truthy: abrir o modal sem
    // SKU escolhido reidratava tudo com string vazia por cima do que já estava
    // digitado. Só um objeto com uuid é um SKU de verdade.
    if (!selectedObject?.skuUUID) return;

    setForm({
      ...FORM_VAZIO,
      skuUUID: selectedObject.skuUUID,
      skuStatus: selectedObject.skuStatus !== false,
      skuType: selectedObject.skuType?.toString() || "",
      skuCode: selectedObject.skuCode || "",
      skuName: selectedObject.skuName || "",
      skuUnitOfMeasure: selectedObject.skuUnitOfMeasure?.toString() || "",
      skuSuggestedPrice: selectedObject.skuSuggestedPrice || "",
      skuBarcode: selectedObject.skuBarcode || "",
      skuCost: selectedObject.skuCost || "",
      skuWeight: selectedObject.skuWeight || "",
      skuDimensions: selectedObject.skuDimensions || "",
      skuNotes: selectedObject.skuNotes || "",
      unidadeCompra: selectedObject.unidadeCompra || "",
      unidadeCompraOutra: selectedObject.unidadeCompraOutra || "",
      unidadeEstoque: selectedObject.unidadeEstoque || "",
      unidadeConsumo: selectedObject.unidadeConsumo || "",
      fatorConversaoCompraEstoque:
        selectedObject.fatorConversaoCompraEstoque ?? "",
      fatorRendimento: selectedObject.fatorRendimento ?? "",
      perecivel: selectedObject.perecivel === true,
      validadeControlada: selectedObject.validadeControlada === true,
      estoqueSeguranca: selectedObject.estoqueSeguranca ?? "",
    });
    setAviso(null);
  }, [selectedObject]);

  const handleLocalChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  // Recalculada a cada tecla: a conversão é conferência, e conferência que só
  // aparece depois de salvar chega tarde.
  const entradaDeConversao: EntradaDeConversao = {
    unidadeCompra: form.unidadeCompra,
    unidadeCompraOutra: form.unidadeCompraOutra,
    unidadeEstoque: form.unidadeEstoque,
    unidadeConsumo: form.unidadeConsumo,
    fatorConversaoCompraEstoque: form.fatorConversaoCompraEstoque,
    fatorRendimento: form.fatorRendimento,
  };
  const linhaDeConversao = montarLinhaDeConversao(entradaDeConversao);

  const handleSubmit = async () => {
    // A unidade de medida entra na conta dos obrigatórios porque o rótulo dela
    // tem asterisco: `required` em <select> não vale nada aqui (não existe
    // <form>, o clique é num botão comum), então a estrela prometia uma trava
    // que ninguém aplicava.
    if (
      !form.skuCode ||
      !form.skuName ||
      !form.skuType ||
      !form.skuUnitOfMeasure
    ) {
      setAviso({
        tom: "erro",
        texto:
          "Preencha os campos obrigatórios: código, nome, tipo de SKU e " +
          "unidade de medida.",
      });
      return;
    }

    // Sem uuid não existe URL: `PATCH organizationInfo/undefined` sai do
    // navegador, chega no servidor e volta erro — a recusa é aqui, antes.
    if (!form.skuUUID) {
      setAviso({
        tom: "erro",
        texto:
          "Este SKU não tem identificador — não dá para montar a URL do " +
          "PATCH /organizationInfo/:uuid. Feche e abra de novo pela lista.",
      });
      return;
    }

    const rendimento = paraNumero(form.fatorRendimento);
    if (rendimento !== null && !rendimentoEhValido(rendimento)) {
      setAviso({ tom: "erro", texto: RENDIMENTO_INVALIDO });
      return;
    }

    const fator = paraNumero(form.fatorConversaoCompraEstoque);
    if (fator !== null && fator <= 0) {
      setAviso({
        tom: "erro",
        texto:
          "O fator de conversão é quanto de estoque vem em UMA unidade de " +
          "compra: tem de ser maior que zero.",
      });
      return;
    }

    const estoqueSeguranca = paraNumero(form.estoqueSeguranca);
    if (estoqueSeguranca !== null && estoqueSeguranca < 0) {
      setAviso({
        tom: "erro",
        texto: "O estoque de segurança não pode ser negativo.",
      });
      return;
    }

    // O texto livre só existe enquanto a unidade for "outra". Trocar para
    // "caixa de 12" depois de ter escrito "bandeja de 6" deixava a frase morta
    // no estado, e ela ia junto no PATCH.
    const paraEnviar = {
      ...form,
      unidadeCompraOutra:
        form.unidadeCompra === VALOR_OUTRA_UNIDADE_DE_COMPRA
          ? form.unidadeCompraOutra.trim()
          : "",
    };

    setSalvando(true);
    try {
      const resultado = await handleSave("updateSKU", paraEnviar);
      // Quem fecha o modal é o shell. Aqui só se mostra a recusa que ele
      // devolveu — fechar por conta própria no erro é a mentira de sempre.
      if (resultado && resultado.ok === false) {
        setAviso({ tom: resultado.tom || "erro", texto: resultado.texto });
      }
    } finally {
      setSalvando(false);
    }
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(num);
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo} large>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-updateskumodal">
            <AlfredoStyles.MaterialIcons>edit</AlfredoStyles.MaterialIcons>
            {selectedObject?.skuUUID
              ? `Editar SKU - ${form.skuCode}`
              : "Novo SKU"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            {/* Status e Informações Básicas */}
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Status e Identificação
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormRow>
                <AlfredoStyles.CheckboxContainer>
                  <AlfredoStyles.CheckboxInput>
                    <input
                      type="checkbox"
                      name="skuStatus"
                      checked={form.skuStatus}
                      onChange={handleLocalChange}
                    />
                    <span className="checkmark"></span>
                  </AlfredoStyles.CheckboxInput>
                  <AlfredoStyles.CheckboxLabel active={form.skuStatus}>
                    <AlfredoStyles.MaterialIcons>
                      {form.skuStatus ? "check_circle" : "cancel"}
                    </AlfredoStyles.MaterialIcons>
                    SKU {form.skuStatus ? "Ativo" : "Inativo"}
                    <span className="tooltip">
                      {form.skuStatus
                        ? "SKU está ativo e disponível para vendas"
                        : "SKU está inativo e não aparecerá nas vendas"}
                    </span>
                  </AlfredoStyles.CheckboxLabel>
                </AlfredoStyles.CheckboxContainer>

                <AlfredoStyles.InfoBadge>
                  <AlfredoStyles.MaterialIcons>
                    fingerprint
                  </AlfredoStyles.MaterialIcons>
                  UUID: {form.skuUUID || "—"}
                </AlfredoStyles.InfoBadge>
              </AlfredoStyles.FormRow>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.TextInput>
                  <input
                    type="text"
                    name="skuCode"
                    value={form.skuCode}
                    onChange={handleLocalChange}
                    required
                  />
                  <label>Código do SKU *</label>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="text"
                    name="skuName"
                    value={form.skuName}
                    onChange={handleLocalChange}
                    required
                  />
                  <label>Nome do SKU *</label>
                </AlfredoStyles.TextInput>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            {/* Classificação */}
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Classificação e Unidades
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.DropdownInput>
                  <select
                    name="skuType"
                    value={form.skuType}
                    onChange={handleLocalChange}
                    required
                  >
                    <option value="" disabled>
                      Selecione o tipo
                    </option>
                    <option value="1">Produto Final</option>
                    <option value="2">Produto Insumo</option>
                    <option value="3">Produto Combo</option>
                    <option value="4">Produto Dividido</option>
                    <option value="5">Embalagem</option>
                  </select>
                  <label>Tipo de SKU *</label>
                </AlfredoStyles.DropdownInput>

                <AlfredoStyles.DropdownInput>
                  <select
                    name="skuUnitOfMeasure"
                    value={form.skuUnitOfMeasure}
                    onChange={handleLocalChange}
                    required
                  >
                    <option value="" disabled>
                      Selecione a unidade
                    </option>
                    <option value="1">Unidade</option>
                    <option value="2">Kg</option>
                    <option value="3">L</option>
                    <option value="4">M</option>
                  </select>
                  <label>Unidade de Medida *</label>
                </AlfredoStyles.DropdownInput>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            {/* Compra, estoque e consumo */}
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Compra, Estoque e Consumo
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.DropdownInput>
                  <select
                    name="unidadeCompra"
                    value={form.unidadeCompra}
                    onChange={handleLocalChange}
                  >
                    <option value="" disabled>
                      Selecione a unidade de compra
                    </option>
                    {UNIDADES_DE_COMPRA.map((opcao) => (
                      <option key={opcao.valor} value={opcao.valor}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </select>
                  <label>Unidade de Compra</label>
                </AlfredoStyles.DropdownInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="text"
                    name="unidadeCompraOutra"
                    value={form.unidadeCompraOutra}
                    onChange={handleLocalChange}
                    disabled={
                      form.unidadeCompra !== VALOR_OUTRA_UNIDADE_DE_COMPRA
                    }
                    placeholder="ex.: bandeja de 6"
                  />
                  <label>Outra unidade de compra</label>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.DropdownInput>
                  <select
                    name="unidadeEstoque"
                    value={form.unidadeEstoque}
                    onChange={handleLocalChange}
                  >
                    <option value="" disabled>
                      Selecione a unidade de estoque
                    </option>
                    {UNIDADES_DE_ESTOQUE.map((opcao) => (
                      <option key={opcao.valor} value={opcao.valor}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </select>
                  <label>Unidade de Estoque</label>
                </AlfredoStyles.DropdownInput>

                <AlfredoStyles.DropdownInput>
                  <select
                    name="unidadeConsumo"
                    value={form.unidadeConsumo}
                    onChange={handleLocalChange}
                  >
                    <option value="" disabled>
                      Selecione a unidade de consumo
                    </option>
                    {UNIDADES_DE_CONSUMO.map((opcao) => (
                      <option key={opcao.valor} value={opcao.valor}>
                        {opcao.rotulo}
                      </option>
                    ))}
                  </select>
                  <label>Unidade de Consumo</label>
                </AlfredoStyles.DropdownInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.000001"
                    name="fatorConversaoCompraEstoque"
                    value={form.fatorConversaoCompraEstoque}
                    onChange={handleLocalChange}
                    placeholder="0,000000"
                  />
                  <label>Fator Compra → Estoque</label>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.000001"
                    name="fatorRendimento"
                    value={form.fatorRendimento}
                    onChange={handleLocalChange}
                    placeholder="0,000000"
                  />
                  <label>Fator de Rendimento</label>
                </AlfredoStyles.TextInput>

                <Casca.CurrencyPreview>
                  <Casca.MaterialIcons>calculate</Casca.MaterialIcons>
                  {linhaDeConversao.texto}
                </Casca.CurrencyPreview>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            {/* Controle de estoque */}
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Controle de Estoque
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.000001"
                    name="estoqueSeguranca"
                    value={form.estoqueSeguranca}
                    onChange={handleLocalChange}
                    placeholder="0,000000"
                  />
                  <label>
                    Estoque de Segurança
                    {form.unidadeEstoque ? ` (${form.unidadeEstoque})` : ""}
                  </label>
                </AlfredoStyles.TextInput>
              </AlfredoStyles.FormGrid>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    name="perecivel"
                    checked={form.perecivel}
                    onChange={handleLocalChange}
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel active={form.perecivel}>
                  Perecível
                  <span className="tooltip">
                    Produto que estraga e exige giro
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    name="validadeControlada"
                    checked={form.validadeControlada}
                    onChange={handleLocalChange}
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel active={form.validadeControlada}>
                  Validade controlada
                  <span className="tooltip">
                    Cada entrada exige data de validade no recebimento
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>
            </AlfredoStyles.FormSection>

            {/* Calculados */}
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Calculados pelo Sistema
              </AlfredoStyles.SectionTitle>

              {/* Travessão, e não zero nem estimativa: estes dois saem de
                  entrada, saída e custo médio — dados que o módulo de movimento
                  ainda não produz. Número inventado aqui vira decisão de compra
                  errada lá fora. */}
              <AlfredoStyles.FormGrid>
                <Casca.LabelInputContainer>
                  <Casca.Label>
                    Custo unitário corrente
                    <Casca.InfoBadge>calculado</Casca.InfoBadge>
                  </Casca.Label>
                  <Casca.Input value="—" readOnly disabled />
                  <Casca.CurrencyPreview>
                    Depende do módulo de movimento, que ainda não existe.
                  </Casca.CurrencyPreview>
                </Casca.LabelInputContainer>

                <Casca.LabelInputContainer>
                  <Casca.Label>
                    Classe ABC
                    <Casca.InfoBadge>calculado</Casca.InfoBadge>
                  </Casca.Label>
                  <Casca.Input value="—" readOnly disabled />
                  <Casca.CurrencyPreview>
                    Depende do módulo de movimento, que ainda não existe.
                  </Casca.CurrencyPreview>
                </Casca.LabelInputContainer>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            {/* Informações Comerciais */}
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Informações Comerciais
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.01"
                    name="skuSuggestedPrice"
                    value={form.skuSuggestedPrice}
                    onChange={handleLocalChange}
                    placeholder="0,00"
                  />
                  <label>Preço Sugerido (R$)</label>
                  <AlfredoStyles.CurrencyPreview>
                    {formatCurrency(form.skuSuggestedPrice)}
                  </AlfredoStyles.CurrencyPreview>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.01"
                    name="skuCost"
                    value={form.skuCost}
                    onChange={handleLocalChange}
                    placeholder="0,00"
                  />
                  <label>Custo (R$)</label>
                  <AlfredoStyles.CurrencyPreview>
                    {formatCurrency(form.skuCost)}
                  </AlfredoStyles.CurrencyPreview>
                </AlfredoStyles.TextInput>
              </AlfredoStyles.FormGrid>
            </AlfredoStyles.FormSection>

            {/* Informações Adicionais */}
            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Informações Adicionais
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.TextInput>
                  <input
                    type="text"
                    name="skuBarcode"
                    value={form.skuBarcode}
                    onChange={handleLocalChange}
                  />
                  <label>Código de Barras</label>
                </AlfredoStyles.TextInput>

                <AlfredoStyles.TextInput>
                  <input
                    type="number"
                    step="0.001"
                    name="skuWeight"
                    value={form.skuWeight}
                    onChange={handleLocalChange}
                  />
                  <label>Peso (kg)</label>
                </AlfredoStyles.TextInput>
              </AlfredoStyles.FormGrid>

              <AlfredoStyles.TextInput>
                <input
                  type="text"
                  name="skuDimensions"
                  value={form.skuDimensions}
                  onChange={handleLocalChange}
                  placeholder="L x A x C (cm)"
                />
                <label>Dimensões</label>
              </AlfredoStyles.TextInput>

              <AlfredoStyles.TextArea>
                <textarea
                  name="skuNotes"
                  value={form.skuNotes}
                  onChange={handleLocalChange}
                  placeholder="Observações internas sobre este SKU..."
                  rows={3}
                />
                <label>Observações</label>
              </AlfredoStyles.TextArea>
            </AlfredoStyles.FormSection>

            <Casca.AlertMessage $tom="atencao">
              {AVISO_LISTA_FECHADA}
            </Casca.AlertMessage>

            {aviso && (
              <Casca.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </Casca.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando ? "Salvando..." : "Atualizar SKU"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default UpdateSKUModal;
